import { useContext, useEffect, useMemo, useState } from "react";
import { Column, usePagination, useSortBy, useTable } from "react-table";
import { getDefaultPageSize } from "../../../../helpers/utils";
import NoData from "../../../../component/NoData";
import { TimeRangeContext } from "../../../../context/TimeRangeContext";
import TableStatisticsType from "../../../../types/ObjectBrowser/TableStatisticsType";
import IndexStatisticsType from "../../../../types/ObjectBrowser/IndexStatisticsType";
import ObjectTuningApi from "../../../../api/object-tuning/ObjectTuningApi";
import Helper from "../../../../helpers/Helper";

const IndexesTable = (props: { selectedItem: any, setSelectedObject: Function }) => {

    const timeRangeContext = useContext(TimeRangeContext)
    const [tableStatistics, setTableStatistics] = useState<TableStatisticsType[] | IndexStatisticsType[]>([]);
    const [loading, setLoading] = useState(true);
    const isIndex = props.selectedItem.objectType === 'Index'

    useEffect(() => {
        const getTableStatistic = async () => {
            const tableStatisticsData = await ObjectTuningApi.getTableStatistic(timeRangeContext, props.selectedItem.instanceId, props.selectedItem)
            setTableStatistics(tableStatisticsData)
            setLoading(false)
        }

        const getIndexStatistic = async () => {
            const indexStatisticData = await ObjectTuningApi.getIndexStatistic(timeRangeContext, props.selectedItem.instanceId, props.selectedItem)
            setTableStatistics(indexStatisticData)
            setLoading(false)
        }
        if (isIndex) {
            void getTableStatistic()
        } else {
            void getIndexStatistic()
        }

    }, [timeRangeContext, props.selectedItem]);

    const onRowClick = (row: any) => {

        const mapTableItem = {
            tablename: row.tablename,
            tableschema: row.tableschema,
            database: row.database,
            objectType: 'Table',
            rows: row.rows,
            tablesizebytes: row.tablesizebytes,
            indexsizebytes: row.indexsizebytes,
            instanceId: props.selectedItem.instanceId
        };

        const mapIndexItem = {
            objectType: "Index",
            indexname: row.indexname,
            database: row.database,
            indexsizebytes: row.indexsizebytes,
            tablename: row.tablename,
            tableschema: row.tableschema,
            indexschema: row.indexschema,
            tablesizebytes: row.tablesizebytes ?? null,
            rows: null,
            instanceId: props.selectedItem.instanceId
        };


        props.setSelectedObject(isIndex ? mapTableItem : mapIndexItem);
    };

    const definitionColumns: Column[] = useMemo(() => [
        ...(!isIndex ? [
            {
                Header: 'Index',
                headerClassName: "text-nowrap",
                accessor: 'indexname',
                Cell: ({ row, value }: { row: any, value: string }) => (
                        <span className='panel-link' onClick={() => onRowClick(row.original)}>
                            {value}
                    </span>
                ),
            },
            {
                Header: 'Schema',
                headerClassName: "text-nowrap",
                accessor: 'indexschema'
            },
            {
                Header: 'Database',
                accessor: 'database',
                headerClassName: "text-nowrap",
            },
            {
                Header: "Index Size (MB)",
                accessor: "indexsizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                // @ts-ignore
                Cell: ({ value }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            }
        ] : []),
        ...(isIndex ? [
            {
                Header: 'Table',
                headerClassName: "text-nowrap",
                accessor: 'tablename',
                Cell: ({ row, value }: { row: any, value: string }) => (
                        <span className='panel-link' onClick={() => onRowClick(row.original)}>
                            {value}
                    </span>
                ),
            },
            {
                Header: 'Schema',
                headerClassName: "text-nowrap",
                accessor: 'tableschema'
            },
            {
                Header: 'Database',
                accessor: 'database',
                headerClassName: "text-nowrap",
            },
            {
                Header: "Table Size (MB)",
                accessor: "tablesizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                // @ts-ignore
                Cell: ({ value }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            },
            {
                Header: "Total Index Size (MB)",
                accessor: "indexsizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                // @ts-ignore
                Cell: ({ value }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            },
            {
                Header: "Total Size (MB)",
                accessor: "totalsizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                // @ts-ignore
                Cell: ({ value }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            },
            {
                Header: 'Rows',
                headerClassName: "text-nowrap",
                accessor: 'rows',
                className: "text-end",
            },

            // {
            //     Header: 'Table Schema',
            //     headerClassName: "text-nowrap",
            //     accessor: 'tableschema'
            // }
        ] : []),
    ], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page
    } = useTable({
                columns: definitionColumns,
                data: tableStatistics,
                initialState: {
                    pageIndex: 0,
                    pageSize: getDefaultPageSize(),
                }
            },
            useSortBy, usePagination);

    return (
            <div className="row row-cols-1 object-browser">
                <div className="col">
                    <div className="card">
                        <div className="card-header">
                            <i className={props.selectedItem.objectType === 'Table' ? "fa fa-list-ol" : "fa fa-table"} style={{ color: "#2a6ac7", fontSize: 18, marginRight: 8 }}
                               aria-hidden="true"></i>{props.selectedItem.objectType === 'Table' ? 'Indexes' : 'Table'}
                            <span className="badge bg-info"
                                  data-tip="Total Columns count">{Helper.getFormattedBadgeCount(tableStatistics.length)}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseObjectIndexes" aria-expanded="false"
                               aria-controls="collapseObjectIndexes"></i>
                        </div>
                        <div id="collapseObjectIndexes" className="card-body collapse show" style={{ padding: 20 }}>
                            <div className="table-responsive">
                                <table {...getTableProps()} className="table">
                                    <thead>
                                    {headerGroups.map((headerGroup, index) => (
                                            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                                                {headerGroup.headers.map((column, index) => (
                                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                            className={(column as any).className} key={index}>
                                                            {column.render('Header')}
                                                            {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                                            <i className="fal fa-sort-amount-up-alt"/> :
                                                                            <i className="fal fa-sort-amount-down-alt"/>) :
                                                                    <i className="fal fa-sort-amount-down-alt text-light"/>) : ''}
                                                        </th>
                                                ))}
                                            </tr>
                                    ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()}>
                                    {page.map((row, index) => {
                                        prepareRow(row)
                                        return (
                                                <tr {...row.getRowProps()} key={index}>
                                                    {row.cells.map((cell) => {
                                                        return (
                                                                <td {...cell.getCellProps()} className={(cell.column as any).className}
                                                                    style={{ borderLeft: (cell.column as any).color }}>
                                                                    {cell.render('Cell')}
                                                                </td>
                                                        )
                                                    })}
                                                </tr>
                                        )
                                    })}
                                    </tbody>
                                </table>
                                <NoData
                                        error={null}
                                        loading={loading}
                                        length={tableStatistics.length}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    );
}

export default IndexesTable;
